import axios from "axios";

// Phase APEX-C (2026-06-30) — BULLETPROOF same-origin enforcement.
//
// Hard-codes the API base to a RELATIVE ``/api`` path in production
// builds so the FE always calls the same origin that served the SPA.
// Eliminates the apex/www CORS preflight 308 class of outage that
// caused INC-2026-06-29-01, INC-2026-06-30-01, INC-2026-06-30-02
// (three incidents over the same env-var bake-in).
//
// The Emergent deploy pipeline used to bake the prod build's
// ``REACT_APP_BACKEND_URL`` from the deployment's primary-domain
// config (which was the bare apex), and that value is NOT editable
// via the platform's Custom Keys panel. After three failed deploys
// trying to fix the env var, we removed the dependency entirely.
//
// Development gate: ``yarn start`` (NODE_ENV=development) still
// honours ``REACT_APP_BACKEND_URL`` for the rare local workflow where
// FE runs on :3000 and BE on :8001 with no proxy.
//
// DO NOT reintroduce the env-var dependency in production builds.
// Same-origin is correct for every Emergent deployment topology
// (preview, prod, custom domains, future tenants).
const RAW_BACKEND_URL = process.env.NODE_ENV === "development"
    ? (process.env.REACT_APP_BACKEND_URL || "")
    : "";
export const BACKEND_URL = RAW_BACKEND_URL.replace(/\/+$/, "");
export const API_BASE_URL = `${BACKEND_URL}/api`;

// Phase APEX-C — shared accessor. Every FE callsite that needs the
// backend origin MUST import this instead of reading
// ``process.env.REACT_APP_BACKEND_URL`` directly. Returns "" in prod
// (relative URLs → same origin). Do NOT reintroduce env-var reads.
export function getBackendBase() {
    return BACKEND_URL;
}

const api = axios.create({
    baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("rp_token");
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Phase REMEMBER-ME-FIX (2026-07-08) — extracted decision helper so
// the interceptor logic is unit-testable without spinning up axios.
// See phase_remember_me_fix.test.js for the full test matrix.
//
// Returns an object describing what the interceptor should do:
//   { logout: bool, reason: "max_lifetime"|"revoked"|"" }
// The interceptor never nukes the token when ``logout`` is false — the
// caller (e.g. PermissionOverrideModal) handles the 401 itself.
const NON_TERMINAL_401_CODES = new Set([
    "fresh_auth_required",
    "permission_locked",  // future-proof (429/423 today, but harmless here)
]);

export function classify401(detail) {
    // Structured detail with a ``code`` field wins over a bare string.
    const code = typeof detail === "string" ? detail : (detail?.code || "");
    if (code && NON_TERMINAL_401_CODES.has(code)) {
        return { logout: false, reason: "" };
    }
    if (typeof detail === "string") {
        // Defensive substring guard so a future free-form message
        // that still carries the marker doesn't trigger a false logout.
        if (detail.toLowerCase().includes("fresh_auth_required")) {
            return { logout: false, reason: "" };
        }
        if (detail === "session_max_lifetime_reached") {
            return { logout: true, reason: "max_lifetime" };
        }
        if (detail.toLowerCase().includes("revoked")) {
            return { logout: true, reason: "revoked" };
        }
    }
    // Unknown / null detail — the safe default is "session dead, log out".
    return { logout: true, reason: "" };
}

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401) {
            const path = window.location.pathname;
            const isAuthPage = path === "/login" || path === "/signup";
            // Phase H.5 — if we were mid-impersonation, auto-return to the
            // super-admin session instead of kicking to /login. A saved
            // original-token survives in `sa_original_token`.
            const saOriginal = localStorage.getItem("sa_original_token");
            if (!isAuthPage && saOriginal) {
                localStorage.setItem("rp_token", saOriginal);
                localStorage.removeItem("sa_original_token");
                localStorage.removeItem("sa_return_path");
                // Flash a sessionStorage flag so the landing page can show a
                // toast after the reload without relying on component state.
                sessionStorage.setItem("impersonation_expired", "1");
                window.location.assign("/admin");
                return Promise.reject(error);
            }
            if (!isAuthPage && localStorage.getItem("rp_token")) {
                const decision = classify401(error?.response?.data?.detail);
                if (!decision.logout) {
                    // Step-up prompt — the caller (e.g. PermissionOverrideModal)
                    // renders its own re-auth UI. Do NOT nuke the token.
                    return Promise.reject(error);
                }
                if (decision.reason) {
                    sessionStorage.setItem("session_end_reason", decision.reason);
                }
                localStorage.removeItem("rp_token");
                window.location.assign("/login");
            }
        }
        // Task 18 Phase F — broadcast 410 file-missing events as a window
        // CustomEvent so the global FileMissingPostDeployListener (mounted
        // in App.jsx) can render the friendly modal instead of letting
        // callers print "Request failed with status code 410". The detail
        // payload carries the structured info the BE emits.
        if (error?.response?.status === 410) {
            const detail = error?.response?.data?.detail;
            const code = (typeof detail === "object" ? detail?.code : null) || null;
            if (code === "file_missing_post_deploy") {
                try {
                    window.dispatchEvent(new CustomEvent("rp:file-missing", {
                        detail: {
                            filename: detail?.filename || null,
                            url: error?.config?.url || null,
                        },
                    }));
                } catch { /* old browsers */ }
            }
        }
        return Promise.reject(error);
    }
);

export { formatApiError, formatBlobApiError } from "./formatApiError";

export default api;
